if (!customElements.get('video-player-component')) {
  const streamAudioProbes = new Map();

  /**
   * Reads an HLS master playlist for an audio rendition.
   * @param {string} url
   * @returns {Promise<boolean | null>} `null` when the manifest cannot answer.
   */
  const streamHasAudio = (url) => {
    if (!streamAudioProbes.has(url)) {
      const probe = fetch(url, { credentials: 'omit' })
        .then((response) => (response.ok ? response.text() : null))
        .then((manifest) => {
          if (!manifest?.includes('#EXT-X-STREAM-INF')) return null;
          if (manifest.includes('TYPE=AUDIO')) return true;
          const codecLists = [...manifest.matchAll(/CODECS="([^"]*)"/g)].map((match) => match[1]);
          if (codecLists.length === 0) return null;
          return codecLists.some((list) => /(^|,)\s*(mp4a|ac-3|ec-3|opus|flac|mp3)/i.test(list));
        })
        .catch(() => null);
      streamAudioProbes.set(url, probe);
    }
    return streamAudioProbes.get(url);
  };

  /**
   * @param {number} seconds
   * @returns {string}
   */
  const formatTime = (seconds) => {
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = String(totalSeconds % 60).padStart(2, '0');
    if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${remainingSeconds}`;
    return `${minutes}:${remainingSeconds}`;
  };

  class VideoPlayerComponent extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.provider = this.dataset.videoProvider || 'html5';
      this.autoplay = this.dataset.autoplay === 'true';
      this.providerApi = this.dataset.providerApi === 'true';
      this.customControls = this.hasAttribute('data-custom-controls');
      this.managedAutoplay = this.dataset.managedAutoplay === 'true';
      this.motionPreference = window.DieuxFrame?.motionQuery ?? window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.audioStarted = false;
      this.desiredMuted = undefined;
      this.providerFocused = false;

      this.posterButton = this.querySelector('[data-video-action="load"]');
      this.toggleButton = this.querySelector('[data-video-action="toggle"]');
      this.controls = this.querySelector('[data-video-ref="controls"]');
      this.scrubber = this.querySelector('[data-video-ref="scrubber"]');
      this.muteButton = this.querySelector('[data-video-ref="sound"]');
      this.mediaContainer = this.querySelector('[data-video-ref="media"]');
      this.template = this.querySelector('[data-video-ref="template"]');

      this.posterButton?.addEventListener('click', (event) => this.load(event.detail === 0), { signal });
      this.toggleButton?.addEventListener('click', () => this.toggle(), { signal });
      this.scrubber?.addEventListener('input', () => this.seekFromScrubber(), { signal });
      this.scrubber?.addEventListener('pointerdown', (event) => this.beginScrub(event), { signal });
      this.muteButton?.addEventListener('click', () => this.toggleMuted(), { signal });
      document.addEventListener('dieux:video:play', (event) => this.pauseForPeer(event), { signal });
      document.addEventListener('close', (event) => this.pauseForDialog(event), { capture: true, signal });
      document.addEventListener('visibilitychange', () => this.handleVisibility(), { signal });
      this.motionPreference.addEventListener('change', () => this.handleMotionPreference(), { signal });

      this.detectAudio();
      this.syncMuteControl();
      this.observeViewport();
      this.attachMedia(false);
    }

    disconnectedCallback() {
      this.pause();
      this.endScrub();
      this.viewportObserver?.disconnect();
      this.abortController.abort();
    }

    observeViewport() {
      if (!this.autoplay || this.managedAutoplay || this.provider !== 'html5') return;

      this.viewportObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.playAutoplay();
          else this.pause();
        },
        { threshold: 0.25 }
      );
      this.viewportObserver.observe(this);
    }

    beginScrub(event) {
      if (!(this.scrubber instanceof HTMLInputElement) || this.scrubber.disabled) return;
      if (event.button !== 0 || !event.isPrimary) return;

      this.endScrub();
      const controller = new AbortController();
      const { signal } = controller;
      const { pointerId } = event;
      // One read per gesture: the line does not move under the drag.
      const bounds = this.scrubber.getBoundingClientRect();
      this.scrubController = controller;
      this.setAttribute('data-video-scrubbing', '');
      this.scrubToPointer(event.clientX, bounds);
      document.addEventListener(
        'pointermove',
        (moveEvent) => {
          if (moveEvent.pointerId === pointerId) this.scrubToPointer(moveEvent.clientX, bounds);
        },
        { passive: true, signal }
      );
      const release = (releaseEvent) => {
        if (releaseEvent.pointerId === pointerId) this.endScrub();
      };
      document.addEventListener('pointerup', release, { passive: true, signal });
      document.addEventListener('pointercancel', release, { passive: true, signal });
    }

    scrubToPointer(clientX, bounds) {
      if (!(this.scrubber instanceof HTMLInputElement) || !(bounds.width > 0)) return;
      const max = Number(this.scrubber.max);
      if (!Number.isFinite(max) || max <= 0) return;

      const fraction = Math.min(Math.max((clientX - bounds.left) / bounds.width, 0), 1);
      this.scrubber.value = String(fraction * max);
      this.seekFromScrubber();
    }

    endScrub() {
      this.scrubController?.abort();
      this.scrubController = undefined;
      this.removeAttribute('data-video-scrubbing');
    }

    load(focusControl) {
      if (this.mediaElement) {
        this.play();
        return;
      }
      if (!this.hydrate()) return;

      this.attachMedia(focusControl);
      if (this.mediaElement instanceof HTMLVideoElement) this.play();
      else this.showMedia();
    }

    /** Attaches a deferred uploaded video paused, so its source buffers before playback is asked for. */
    warm() {
      if (this.mediaElement || this.provider !== 'html5' || !this.hydrate()) return;
      this.attachMedia(false);
      this.pause();
    }

    hydrate() {
      if (!(this.template instanceof HTMLTemplateElement) || !(this.mediaContainer instanceof HTMLElement)) return false;
      this.mediaContainer.replaceChildren(this.template.content.cloneNode(true));
      return true;
    }

    attachMedia(focusControl) {
      const element = this.mediaContainer?.querySelector('[data-video-ref="element"]');
      const { signal } = this.abortController;

      if (element instanceof HTMLIFrameElement) {
        this.mediaElement = element;
        element.addEventListener('load', () => this.syncProviderPlayback(focusControl), { signal });
        requestAnimationFrame(() => this.syncProviderPlayback(focusControl));
        return;
      }
      if (!(element instanceof HTMLVideoElement)) return;

      this.mediaElement = element;
      element.controls = !this.customControls;
      if (this.desiredMuted !== undefined) element.muted = this.desiredMuted;

      const on = (types, handler) => {
        const listener = () => {
          if (this.mediaElement === element) handler();
        };
        for (const type of types.split(' ')) element.addEventListener(type, listener, { signal });
      };
      on('click', () => this.toggle());
      on('play', () => this.setState('playing'));
      on('pause ended', () => this.setState('paused'));
      on('loadedmetadata durationchange', () => this.syncDuration());
      on('timeupdate seeking seeked', () => this.syncProgress());
      on('volumechange', () => this.syncMuteControl());
      on('error', () => this.handleError());

      this.revealOnFrames(element);
      this.setState(element.paused ? 'paused' : 'playing');
      this.syncDuration();

      if (this.autoplay && !this.managedAutoplay && !this.motionPreference.matches && !document.hidden) {
        this.play();
      }
      if (focusControl) requestAnimationFrame(() => this.toggleButton?.focus({ preventScroll: true }));
    }

    /** Shows the video once it has presented a second frame of its own; until then the preview image shows through. */
    revealOnFrames(video) {
      const reveal = () => {
        if (this.mediaElement === video) this.showMedia();
      };
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(() => video.requestVideoFrameCallback(reveal));
      } else {
        video.addEventListener('playing', reveal, { once: true, signal: this.abortController.signal });
      }
    }

    showMedia() {
      this.toggleAttribute('data-video-shown', true);
    }

    /** Hands the frame back to the poster and its play control when the browser refuses to start playback. */
    showPoster() {
      this.removeAttribute('data-video-shown');
      if (this.mediaElement instanceof HTMLVideoElement) this.revealOnFrames(this.mediaElement);
    }

    syncProviderPlayback(focusControl) {
      const shouldPlay = this.dataset.startOnLoad === 'true'
        && (!this.autoplay || !this.motionPreference.matches)
        && !document.hidden;

      if (this.providerApi) {
        if (shouldPlay) this.play();
        else this.pause();
      }
      if (focusControl && !this.providerFocused) {
        this.providerFocused = true;
        requestAnimationFrame(() => {
          if (this.customControls) this.toggleButton?.focus({ preventScroll: true });
          else this.mediaElement?.focus({ preventScroll: true });
        });
      }
    }

    mediaDuration() {
      const video = this.mediaElement;
      if (!(video instanceof HTMLVideoElement) || !Number.isFinite(video.duration) || video.duration <= 0) return 0;
      return video.duration;
    }

    seekFromScrubber() {
      const duration = this.mediaDuration();
      const requestedTime = Number(this.scrubber?.value);
      if (!duration || !Number.isFinite(requestedTime)) return;

      this.mediaElement.currentTime = Math.min(Math.max(requestedTime, 0), duration);
      this.syncProgress();
    }

    syncDuration() {
      if (!(this.scrubber instanceof HTMLInputElement)) return;

      const duration = this.mediaDuration();
      this.scrubber.disabled = duration === 0;
      this.scrubber.max = String(duration);
      this.syncProgress();
    }

    syncProgress() {
      if (!(this.scrubber instanceof HTMLInputElement)) return;

      const duration = this.mediaDuration();
      const currentTime = duration ? Math.min(Math.max(this.mediaElement.currentTime, 0), duration) : 0;
      const progress = `${duration ? (currentTime / duration) * 100 : 0}%`;
      const valueText = `${formatTime(currentTime)} / ${formatTime(duration)}`;

      this.scrubber.value = String(currentTime);
      if (this.scrubber.style.getPropertyValue('--video-progress') !== progress) {
        this.scrubber.style.setProperty('--video-progress', progress);
      }
      if (this.scrubber.getAttribute('aria-valuetext') !== valueText) {
        this.scrubber.setAttribute('aria-valuetext', valueText);
      }
    }

    toggleMuted() {
      const unmuting = this.isMuted();
      const firstAudio = unmuting && !this.audioStarted;
      this.setMuted(!unmuting);
      if (firstAudio) this.restartForAudio();
      this.syncMuteControl();
    }

    restartForAudio() {
      if (!this.autoplay || !(this.mediaElement instanceof HTMLVideoElement)) return;

      this.mediaElement.currentTime = 0;
      this.userPaused = false;
      this.play();
    }

    isMuted() {
      if (this.mediaElement instanceof HTMLVideoElement) return this.mediaElement.muted;
      return this.desiredMuted ?? this.autoplay;
    }

    setMuted(muted) {
      this.desiredMuted = muted;
      if (!muted) this.audioStarted = true;
      if (this.mediaElement instanceof HTMLVideoElement) this.mediaElement.muted = muted;
    }

    detectAudio() {
      if (this.provider !== 'html5' || !(this.muteButton instanceof HTMLButtonElement)) return;

      const selector = 'source[src*=".m3u8"]';
      const stream = this.mediaContainer?.querySelector(selector) ?? this.template?.content.querySelector(selector);
      const streamSrc = stream?.getAttribute('src');
      if (!streamSrc) return;

      const { signal } = this.abortController;
      streamHasAudio(new URL(streamSrc, document.baseURI).href).then((hasAudio) => {
        if (signal.aborted || hasAudio !== false) return;
        this.hasAudio = false;
        this.syncMuteControl();
      });
    }

    syncMuteControl() {
      const button = this.muteButton;
      if (!(button instanceof HTMLButtonElement)) return;

      const silent = this.hasAudio === false;
      if (button.hidden !== silent) {
        if (silent && button.contains(document.activeElement)) this.toggleButton?.focus({ preventScroll: true });
        button.hidden = silent;
      }

      const muted = this.isMuted();
      const label = muted ? button.dataset.unmuteLabel : button.dataset.muteLabel;
      button.disabled = this.dataset.state === 'error';
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      this.toggleAttribute('data-video-muted', muted);
    }

    toggle() {
      if (this.dataset.state === 'playing') {
        this.userPaused = true;
        this.pause();
        return;
      }

      this.userPaused = false;
      this.setMuted(false);
      this.syncMuteControl();
      if (this.mediaElement) this.play();
      else this.load(true);
    }

    play() {
      const media = this.mediaElement;
      if (media instanceof HTMLVideoElement) {
        media.play().catch((error) => {
          this.setState('paused');
          if (error?.name === 'NotAllowedError') this.showPoster();
        });
      } else if (media instanceof HTMLIFrameElement && this.providerApi) {
        this.sendProviderCommand('play');
        this.setState('playing');
      }
    }

    playAutoplay() {
      if (!this.autoplay || this.userPaused || this.motionPreference.matches) return;
      if (document.hidden) {
        this.resumeWhenVisible = true;
        return;
      }
      if (!this.mediaElement) {
        this.load(false);
        return;
      }
      this.play();
    }

    pause() {
      const media = this.mediaElement;
      if (media instanceof HTMLVideoElement) {
        media.pause();
      } else if (media instanceof HTMLIFrameElement && this.providerApi) {
        this.sendProviderCommand('pause');
        this.setState('paused');
      }
    }

    sendProviderCommand(action) {
      if (!(this.mediaElement instanceof HTMLIFrameElement) || !this.mediaElement.contentWindow) return;

      let targetOrigin = '*';
      try {
        targetOrigin = new URL(this.mediaElement.src).origin;
      } catch (_error) {}

      const message = this.provider === 'youtube'
        ? JSON.stringify({ event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: [] })
        : { method: action };
      this.mediaElement.contentWindow.postMessage(message, targetOrigin);
    }

    setState(state) {
      if (this.dataset.state === state) return;
      this.dataset.state = state;

      const failed = state === 'error';
      const playing = state === 'playing';
      if (this.customControls) {
        if (this.controls instanceof HTMLElement) this.controls.hidden = failed;
        if (this.scrubber instanceof HTMLElement) this.scrubber.hidden = failed;
      }
      if (this.toggleButton instanceof HTMLButtonElement) {
        this.toggleButton.disabled = failed;
        this.toggleButton.setAttribute(
          'aria-label',
          playing ? this.toggleButton.dataset.pauseLabel : this.toggleButton.dataset.playLabel
        );
      }
      this.syncMuteControl();

      if (playing) document.dispatchEvent(new CustomEvent('dieux:video:play', { detail: { player: this } }));
    }

    pauseForPeer(event) {
      if (event.detail?.player === this || this.dataset.state !== 'playing') return;
      if (!this.managedAutoplay) this.userPaused = true;
      this.pause();
    }

    pauseForDialog(event) {
      if (event.target instanceof HTMLDialogElement && event.target.contains(this)) this.pause();
    }

    handleError() {
      const restoreFocus = this.controls?.contains(document.activeElement);
      this.setState('error');
      this.removeAttribute('data-video-shown');
      this.mediaContainer?.replaceChildren();
      this.mediaElement = undefined;
      this.syncDuration();
      if (restoreFocus) this.posterButton?.focus();
    }

    handleVisibility() {
      if (document.hidden) {
        this.resumeWhenVisible = this.autoplay && !this.userPaused && this.dataset.state === 'playing';
        this.pause();
        return;
      }
      if (this.resumeWhenVisible) this.playAutoplay();
      this.resumeWhenVisible = false;
    }

    handleMotionPreference() {
      if (this.motionPreference.matches && this.autoplay && this.dataset.state === 'playing') this.pause();
    }
  }

  customElements.define('video-player-component', VideoPlayerComponent);
}
